import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { IMetaPage, MetaIntegration, TMetaIntegrationStatus } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { errorMessage, logError } from '@feathr/hooks';

/** Interval (ms) between automatic refetches while status is `configuring`. */
export const CONFIGURING_POLL_INTERVAL_MS = 30_000;

/** Structured error with a specific title for display */
export interface IMetaIntegrationError {
  /** Short, specific title describing the error category */
  title: string;
  /** Detailed error message */
  message: string;
}

/** Return type for the useMetaIntegration hook */
export interface IUseMetaIntegration {
  /** Whether the integration data is being fetched */
  isLoading: boolean;
  /**
   * Backend-derived integration status, or `null` before the first fetch resolves
   * or when the fetch itself failed. Consumers should branch on this for any
   * status-specific UI.
   */
  status: TMetaIntegrationStatus | null;
  /**
   * Structured error surfaced to the page-top Alert. The first three sources
   * are mutually exclusive branches inside `doFetch`; any of them overwrites a
   * stale OAuth redirect error (source 4) that was set on mount:
   * - Backend `setup_error` when `status === 'error'`
   * - A partial-failure when the pages endpoint fails after integration loaded
   * - A generic fetch error when the integration request fails
   * - An OAuth redirect error from URL query params (set before fetch;
   *   survives only if the fetch produces no error)
   */
  error: IMetaIntegrationError | null;
  /** The MetaIntegration model instance, null before loaded or when fetch failed */
  integration: MetaIntegration | null;
  /** List of connected Facebook pages */
  pages: IMetaPage[];
  /** Callback to refetch integration data */
  refetch: () => Promise<void>;
}

/**
 * Hook to manage Meta integration state and data fetching.
 *
 * Handles fetching integration status, pages data, and OAuth error detection
 * from query parameters after redirect. Auto-polls the backend every
 * `CONFIGURING_POLL_INTERVAL_MS` while `status === 'configuring'` so users see
 * the transition to a terminal state without manual refresh.
 *
 * @returns Integration state including loading, status, error, and data.
 * @example
 * const { status, integration, pages, refetch } = useMetaIntegration();
 */
export function useMetaIntegration(): IUseMetaIntegration {
  const { t } = useTranslation();
  const location = useLocation();
  const { MetaIntegrations } = useStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<IMetaIntegrationError | null>(null);
  const [integration, setIntegration] = useState<MetaIntegration | null>(null);
  const [pages, setPages] = useState<IMetaPage[]>([]);

  // Tracks the latest fetch call so stale responses from earlier calls are discarded
  const fetchIdRef = useRef(0);

  /**
   * Shared fetch logic used by the mount effect, the refetch callback, and the
   * configuring-state poller.
   *
   * @param shouldCancel - Cancellation check; callers use a closure
   *   (`isCancelled` flag) or a ref comparison (`fetchIdRef`) so stale
   *   responses don't update state.
   * @param pendingOauthError - The OAuth redirect error just set by the mount
   *   effect, if any. Used only for telemetry when the backend status is
   *   `error` so we can log the collision; defaults to `null` for `refetch`
   *   and poll callers that have no OAuth context.
   * @param silent - When true, skip toggling `isLoading` and clearing `pages`
   *   at the start of the fetch. Used by the configuring-state poller so
   *   background refreshes don't wipe the connected UI every interval.
   */
  async function doFetch(
    shouldCancel: () => boolean,
    pendingOauthError: string | null = null,
    { silent = false }: { silent?: boolean } = {},
  ): Promise<void> {
    if (!silent) {
      setIsLoading(true);
      setPages([]);
    }

    try {
      const metaIntegration = await MetaIntegrations.fetchIntegration();
      if (shouldCancel()) {
        return;
      }
      setIntegration(metaIntegration);

      if (metaIntegration.isError) {
        // The OAuth URL error, if any, is intentionally overwritten: the
        // backend reached a persisted setup_error, which is more authoritative
        // than a redirect param that may be stale. Log the collision for
        // support visibility when both errors are present.
        if (pendingOauthError) {
          logError({
            message: 'Meta OAuth error overwritten by backend setup error',
            level: 'warning',
            extra: { oauthError: pendingOauthError, integrationId: metaIntegration.id },
          });
        }
        // The backend shouldn't reach `status === 'error'` without setting
        // `setup_error`; if it does, surface a generic message but flag the
        // missing field so support has context.
        if (!metaIntegration.setupError) {
          logError({
            message: 'Meta integration in error state without setup_error',
            level: 'warning',
            extra: { integrationId: metaIntegration.id },
          });
        }
        setError({
          title: t('Meta setup failed'),
          message:
            metaIntegration.setupError ??
            t("Meta couldn't finish configuring your account. Please contact support."),
        });
      }

      // Only fetch pages if the integration has progressed past initial setup
      if (metaIntegration.isConnected || metaIntegration.isConfiguring) {
        try {
          const pagesData = await metaIntegration.getPages();
          if (shouldCancel()) {
            return;
          }
          setPages(pagesData);
        } catch (error_) {
          if (shouldCancel()) {
            return;
          }
          const pagesErrorMsg = errorMessage(error_, t);
          logError({
            message: 'Meta pages fetch failed',
            level: 'warning',
            extra: { error: pagesErrorMsg },
          });
          // Partial failure: integration loaded but pages didn't
          setError({
            title: t('Failed to Load Pages'),
            message: t('Connected, but failed to load Facebook pages'),
          });
        }
      } else if (silent) {
        // Silent refetches skip the top-level `setPages([])`, so explicitly
        // clear here when the new state has no pages (e.g. a poll landing on
        // `no_active_pages`). Otherwise stale pages would linger in state.
        setPages([]);
      }
    } catch (error_) {
      if (shouldCancel()) {
        return;
      }
      const errMsg = errorMessage(error_, t);
      logError({
        message: 'Meta integration fetch failed',
        level: 'error',
        extra: { error: errMsg },
      });
      setError({ title: t('Connection Error'), message: errMsg });
    } finally {
      if (!shouldCancel() && !silent) {
        setIsLoading(false);
      }
    }
  }

  // Increment fetchIdRef so any in-flight request from a prior call is ignored
  const refetch = useCallback(async (): Promise<void> => {
    const id = ++fetchIdRef.current;
    setError(null);
    await doFetch(() => fetchIdRef.current !== id);
  }, [MetaIntegrations, t]);

  // Internal poll-only refetch that doesn't toggle `isLoading` or clear
  // `pages`, so the page body and header stay visible while the backend
  // is still reaching a terminal state.
  const refetchSilently = useCallback(async (): Promise<void> => {
    const id = ++fetchIdRef.current;
    setError(null);
    await doFetch(() => fetchIdRef.current !== id, null, { silent: true });
  }, [MetaIntegrations, t]);

  // Runs on mount and whenever the query string changes (e.g. OAuth redirect)
  useEffect(() => {
    let isCancelled = false;

    // Check for OAuth error params that Facebook appends on failed authorization
    const searchParams = new URLSearchParams(location.search);
    const oauthError = searchParams.get('error');
    const oauthErrorDescription = searchParams.get('error_description');
    // Prefer the human-readable description, fall back to the error code
    const oauthErrorMessage = oauthError ? oauthErrorDescription || oauthError : null;

    if (oauthError) {
      logError({
        message: 'Meta OAuth flow returned an error',
        level: 'warning',
        extra: {
          oauthError,
          oauthErrorDescription,
        },
      });
      setError({
        title: t('Authorization Failed'),
        message: oauthErrorMessage ?? oauthError,
      });
    } else {
      setError(null);
    }

    // Always fetch integration data, even when there's an OAuth error,
    // so the page renders the correct connected/onboarding state
    doFetch(() => isCancelled, oauthErrorMessage);

    return () => {
      isCancelled = true;
    };
  }, [location.search, MetaIntegrations, t]);

  const status: TMetaIntegrationStatus | null = integration?.status ?? null;

  // Poll while setup is in progress so the user sees the transition to a
  // terminal state (connected / error / no_active_pages) without a manual
  // refresh. Clean up on status change (effect re-runs) or on unmount.
  useEffect(() => {
    if (status !== 'configuring') {
      return undefined;
    }
    const interval = setInterval(() => {
      // refetchSilently handles its own errors via doFetch's try/catch +
      // setError; the no-op .catch is just defensive against future
      // rejections so the floating-promise lint rule passes without using
      // the `void` operator.
      refetchSilently().catch(() => undefined);
    }, CONFIGURING_POLL_INTERVAL_MS);
    return (): void => clearInterval(interval);
  }, [status, refetchSilently]);

  return {
    isLoading,
    status,
    error,
    integration,
    pages,
    refetch,
  };
}

/** Return type for the useMetaSync hook. */
export interface IUseMetaSync {
  /** Trigger a page sync for the current integration. */
  handleSync: () => Promise<void>;
  /** Whether a sync is currently in progress. */
  isSyncing: boolean;
}

/**
 * Hook to trigger a Meta page synchronization.
 *
 * Encapsulates the sync call, loading state, error handling, and toast
 * notifications so the logic can be shared between `MetaSettingsButton`
 * and the alert banners on the integration page.
 *
 * @param integration - The current MetaIntegration model, or `null` when not loaded.
 * @returns Sync handler and loading flag.
 */
export function useMetaSync(integration: MetaIntegration | null): IUseMetaSync {
  const { t } = useTranslation();
  const [isSyncing, setIsSyncing] = useState(false);

  async function handleSync(): Promise<void> {
    if (!integration) {
      return;
    }
    setIsSyncing(true);
    try {
      await integration.syncPages();
    } catch (error) {
      const errorMsg = errorMessage(error, t);
      const isNoConnectionsError = errorMsg.includes('No active Meta connections');

      if (isNoConnectionsError) {
        toast(
          t(
            'Cannot sync: no active Meta connections found. Try reconnecting your Meta integration.',
          ),
          { type: 'error' },
        );
        return;
      }

      logError({
        message: 'Meta integration page sync failed',
        level: 'error',
        extra: {
          error: errorMsg,
          integrationId: integration.id,
        },
      });
      toast(
        t('Something went wrong while syncing your Meta pages: {{error}}', {
          error: errorMsg,
        }),
        { type: 'error' },
      );
      return;
    } finally {
      setIsSyncing(false);
    }

    toast(t('Meta page sync queued. New pages may take a few minutes to appear.'), {
      type: 'success',
    });
  }

  return { handleSync, isSyncing };
}
